import Moment from "moment";
import Enumerable from "linq";
import { DateDuration, DateTimeStringUtility } from "../../../domain/system";
import { IAiService, IAiPhraseVariable, IAiPhraseInfo, IAiPhraseCreateOptions, IAiTranslationInfo, IAiPhraseRecord } from "../../../domain/externalSystem";
import { AuthenticatedService } from "../..";
import { IAiPhraseInfoBean, IAiPhraseVariableBean, IAiPhraseRecordBean } from "./beans";

export class AiService extends AuthenticatedService implements IAiService {
  constructor(getToken: () => string) {
    super(getToken);
  }

  async createPhrase(template: string, patientNo: string, duration: DateDuration, options?: IAiPhraseCreateOptions): Promise<IAiPhraseInfo> {
    const results =  (await this.post("/api/external-system/ai/create-phrase", {
      template: template,
      patientNo: patientNo,
      duration: {
        from: this.toDateString(duration ? duration.from : null),
        to: this.toDateString(duration ? duration.to : null),
      },
      recipe: options ? options.recipe : undefined,
      departmentIds: options ? options.departmentIds : undefined,
      professionIds: options ? options.professionIds : undefined,
    })).data as IAiPhraseInfoBean;
    return {
      template: results.template,
      variables: this.toVariables(this.extractKeys(results.template), results.variables),
      isError: results.isError,
      text: results.text,
      records: Enumerable.from(results.records).select(r => this.toAiPhraseRecord(r)).toArray(),
    };
  }

  private extractKeys(template: string): string[] {
    // IEはSet非対応.重複除去はreduce+indexOfで.
    const matched = template.match(/{\*?[^}]+}/g);
    return matched
      ? matched
        .map(k => k.replace(/^{\*?/, "").replace(/}$/, ""))
        .reduce<string[]>((ks, k) => {
          if (ks.indexOf(k) < 0) {
            ks.push(k);
          }
          return ks;
        }, [])
      : [];
  }

  private toDateString(source: Date): string {
    return source ? Moment(source).format("YYYYMMDD") : null;
  }

  private toVariables(keys: string[], variables: IAiPhraseVariableBean[]): { [key: string]: IAiPhraseVariable } {
    return variables
      ? keys.reduce<{ [key: string]: IAiPhraseVariable }>((a, k) => {
        a[k] = this.toVariable(k, variables);
        return a;
      }, {})
      : {};
  }

  private toVariable(key: string, variables: IAiPhraseVariableBean[]): IAiPhraseVariable {
    const v = variables.find((s) => s.key === key);
    if (!v) {
      return {
        isValid: false,
        value: "",
        reason: "生成されませんでした。",
      };
    } else if (!v.value) {
      return {
        isValid: false,
        value: "",
        reason: "空文字列が生成されました。",
      };
    } else {
      return {
        isValid: true,
        value: v.value,
        evidences: v.evidences
          ? v.evidences.map(e => ({
            description: e.description,
            at: e.at ? DateTimeStringUtility.toDateTimeValue(e.at) : undefined,
            source: e.source,
            author: e.author ? {
              name: e.author.name,
              role: e.author.role,
            } : undefined,
            department: e.department,
          }))
        : [],
      };
    }
  }

  private toAiPhraseRecord(bean: IAiPhraseRecordBean): IAiPhraseRecord {
    return {
      description: bean.description,
      at: bean.at ? DateTimeStringUtility.toDateTimeValue(bean.at) : undefined,
      source: bean.source,
      author: bean.author ? {
        name: bean.author.name,
        role: bean.author.role,
      } : undefined,
      department: bean.department,
    };
  }

  async translate(input: string, language: string): Promise<IAiTranslationInfo> {
    return (await this.post("/api/external-system/ai/translate", {
      input: input,
      language: language,
    })).data as IAiTranslationInfo;
  }
}
